import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { HANDLING_UNIT_MOVEMENT, HandlingUnitMovementTypeValues, HANDLING_UNIT_MOVEMENT_TYPE_LABELS, } from '../types/handling-unit-movement.js';
import { USER_DATA } from '../types/user.js';
/**
 * Descriptor for HandlingUnitMovement (0140) — read via `/pg/v1`.
 * Written only by system-api `stock/` scope, inside the transaction of the volume op
 * that produced it. Never mutated via `/pg/v1`.
 */
export const handlingUnitMovementDescriptor = {
    name: HANDLING_UNIT_MOVEMENT,
    label: 'Movimento de Volume',
    domain: 'stock',
    collectionPath: 'org/{orgId}/handling-unit-movement',
    pathPatternKeys: { orgId: 'string' },
    version: 2,
    description: 'Immutable physical movement log for a HandlingUnit (volume)',
    repositoryConfig: {
        dataSource: DataSourceStrategy.PostgresRest,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: [DataSourceStrategy.PostgresRest],
    writePolicy: 'readonly',
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'movementDate', label: 'Data', type: 'date', display: 'datetime', required: true },
        {
            name: 'type',
            label: 'Tipo',
            type: 'enum',
            required: true,
            enumValues: [...HandlingUnitMovementTypeValues],
            enumLabels: HANDLING_UNIT_MOVEMENT_TYPE_LABELS,
        },
        { name: 'handlingUnitId', label: 'Volume', type: 'string', required: true },
        { name: 'fromStorageAddressId', label: 'Endereço origem', type: 'string', required: false },
        { name: 'toStorageAddressId', label: 'Endereço destino', type: 'string', required: false },
        { name: 'fromStockLocationId', label: 'Local origem', type: 'string', required: false },
        { name: 'toStockLocationId', label: 'Local destino', type: 'string', required: false },
        {
            name: 'fromContainerHandlingUnitId',
            label: 'Container origem',
            type: 'string',
            required: false,
        },
        {
            name: 'toContainerHandlingUnitId',
            label: 'Container destino',
            type: 'string',
            required: false,
        },
        { name: 'productId', label: 'Produto', type: 'string', required: false },
        { name: 'variantId', label: 'Variante', type: 'string', required: false },
        { name: 'lotId', label: 'Lote', type: 'string', required: false },
        {
            name: 'ownerPartyId',
            label: 'Proprietário',
            type: 'string',
            required: false,
            description: 'FK → Party; null = estoque próprio. Denormalizado (0204).',
        },
        { name: 'quantity', label: 'Quantidade', type: 'number', display: 'quantity', required: true },
        {
            name: 'relatedHandlingUnitIds',
            label: 'Volumes relacionados',
            type: 'object',
            required: false,
        },
        { name: 'stockMovementId', label: 'Movimento de estoque', type: 'string', required: false },
        { name: 'sourceType', label: 'Tipo origem', type: 'string', required: false },
        { name: 'sourceId', label: 'Id origem', type: 'string', required: false },
        { name: 'groupId', label: 'Grupo', type: 'string', required: false },
        {
            name: 'warehouseTaskLineId',
            label: 'Linha de tarefa',
            type: 'string',
            required: false,
        },
        { name: 'reversedMovementId', label: 'Movimento estornado', type: 'string', required: false },
        { name: 'createdBy', label: 'Criado por', type: 'string', required: false },
        { name: 'createdAt', label: 'Criado em', type: 'date', display: 'datetime', required: false },
        { name: 'deviceId', label: 'Dispositivo', type: 'string', required: false },
    ],
    foreignKeys: [
        {
            fieldName: 'handlingUnitId',
            targetEntity: 'HandlingUnit',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'fromStorageAddressId',
            targetEntity: 'StorageAddress',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'toStorageAddressId',
            targetEntity: 'StorageAddress',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'fromStockLocationId',
            targetEntity: 'StockLocation',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'toStockLocationId',
            targetEntity: 'StockLocation',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'fromContainerHandlingUnitId',
            targetEntity: 'HandlingUnit',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'toContainerHandlingUnitId',
            targetEntity: 'HandlingUnit',
            relationship: 'many-to-one',
            displayField: 'code',
        },
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'lotId',
            targetEntity: 'Lot',
            relationship: 'many-to-one',
            displayField: 'lotNumber',
        },
        {
            fieldName: 'ownerPartyId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
        },
        {
            fieldName: 'stockMovementId',
            targetEntity: 'StockMovement',
            relationship: 'many-to-one',
            displayField: 'id',
        },
        {
            fieldName: 'reversedMovementId',
            targetEntity: 'HandlingUnitMovement',
            relationship: 'many-to-one',
            displayField: 'id',
        },
        // `expand: false` — label-only (0167 §11): resolved by the System API, no join.
        {
            fieldName: 'createdBy',
            targetEntity: USER_DATA,
            relationship: 'many-to-one',
            displayField: 'name',
            expand: false,
        },
    ],
};
//# sourceMappingURL=handling-unit-movement.descriptor.js.map